module Meevo.Components.UdfImageUpload {
    import FileMetadata = Meevo.Model.FileMetadata;
    import FileTypes = Meevo.Model.FileTypes;
    import ICustomDataResource = Meevo.Model.ICustomDataResource;
    import IMeevoFile = Meevo.Model.MeevoFile;

    interface IInstuctionVars {
        size: string;
        width: string;
        height: string;
    }

    interface IFile {
        $ngfBlobUrl: string;
        lastModified: number;
        lastModifiedDate: Date;
        name: string;
        size: number;
        type: string;
        webkitRelativePath?: string
    }

    interface IErrorText {
        invalidBig: string;
        invalidSmall: string;
        invalidExecedesDimensions: string;
    }

    interface IConfig {
        pattern: string;
        aspectRatio: number;
        size: {
            min: number;
            max: string;
        };
        width: {
            min: number;
        };
        height: {
            min: number;
        };
    }

    export class UdfImageUploadComponent {
        static $inject = [
            'meevo.components.modal.ModalService',
            "meevo.components.common.DataService",
            "Upload",
            "$q",
            "meevo.components.busy.BusyService"
        ];

        public fileType: number;
        public meevoFile: IMeevoFile;
        public fileGuid: Guid | string;
        public isStorageUpdated: boolean;
        public mimeType?: string;
        public config: IConfig;
        public instructionVars: IInstuctionVars;
        public showCropShape: boolean = false;
        public retrievalInProgress: boolean;
        public customDataResourceObject?: ICustomDataResource;
        public udfImageTemplate?: boolean;
        public udfImageDateUpdated?: string;
        public revertIcon?: boolean;
        public modalTitle: string = !this.udfImageTemplate || this.revertIcon ? 'LABEL_IMAGE_EDITOR' : 'LABEL_PREVIEW_IMAGE';
        public errors: IErrorText;

        constructor(protected modalService: Meevo.Components.Modal.IModalService,
                    private dataService: Meevo.Components.BaseClasses.IDataService,
                    private Upload,
                    private $q: angular.IQService,
                    protected busyService: Meevo.Components.Busy.BusyService) {

            this.instructionVars = {
                size: '5MB',
                width: '100',
                height: '100',
            };

            this.errors = {
                invalidBig: 'ERROR_FILEUPLOAD_INVALIDSIZE_BIG',
                invalidSmall: 'ERROR_FILEUPLOAD_INVALIDSIZE_SMALL',
                invalidExecedesDimensions: 'ERROR_FILEUPLOAD_INVALIDSIZE_DIMENSIONS',
            };

            if (!this.udfImageTemplate) {
                this.fetchConfig();
            }
        }

        fetchConfig() {
            let uri = `/${Meevo.AppSettings.Default.API_ROOT}/storage/configuration/${this.fileType}`;
            let parameters = {
                mimeType: this.mimeType
            };
            this.dataService.customMethodData(uri, 'GET', '', parameters)
                .then(res => {
                    this.meevoFile = res.data;
                    this.config = this.getNgfValidateConfig();
                })
                .finally(() => {
                    this.setShowCropShape();
                });
        }

        getNgfValidateConfig(): IConfig {
            this.config = {
                pattern: '',
                aspectRatio: 1,
                size: { min: 0, max: '5MB' },
                width: { min: -1 },
                height: { min: -1 }
            };

            if (this.meevoFile === undefined) return;

            let fileMd: FileMetadata;
            this.meevoFile.config.filesMetadata.forEach((setting) => {
                if (fileMd === undefined && setting.key === 'Source') {
                    fileMd = setting;
                }
            });

            if (fileMd === undefined) {
                fileMd = this.meevoFile.config.filesMetadata[0];
            }

            if (!isNaN(fileMd.maxSizeMb)) {
                this.config.size.max = fileMd.maxSizeMb + 'MB';
                this.instructionVars.size = fileMd.maxSizeMb + 'MB';
            }

            this.setDimensionValues("height", fileMd, this.config, this.instructionVars);

            this.setDimensionValues("width", fileMd, this.config, this.instructionVars);

            this.config.aspectRatio = this.meevoFile.config.aspectRatio;
            this.config.pattern = this.meevoFile.config.pattern;

            return;
        }

        setDimensionValues(dimension, fileMd, scopeConfig, instruction) {
            if (!isNaN(fileMd[dimension])) {
                scopeConfig[dimension].min = fileMd[dimension];
                instruction[dimension] = `${fileMd[dimension]}`;

                if (fileMd.heightMax !== -1) {
                    scopeConfig[dimension].max = fileMd[dimension + "Max"];
                }
            }
        }

        setShowCropShape() {
            this.showCropShape = false;
            if (this.meevoFile != undefined && this.meevoFile.config != undefined) {
                this.showCropShape = (this.meevoFile.config.cropShape != undefined &&
                    this.meevoFile.config.cropShape !== "");
            }
        }

        uploadFile(file: IFile, croppedData: string): void {
            this.getFileData(file, croppedData).then(data => {
                this.busyService.start();
                let fileData = {
                    file: data,
                    fileName: file.name,
                    fileGuid: this.fileGuid || NewGuid(),
                    requestType: this.fileType,
                    mimeType: this.mimeType
                };
                this.dataService.addFile(this.meevoFile.config.endPoint, fileData)
                    .then((response: ICustomDataResource) => {
                        if ((FileTypes[this.fileType] != null ||
                            FileTypes[this.fileType] != undefined) &&
                            typeof response.meevoFile !== 'undefined'
                        ) {
                            response.fileGuid = fileData.fileGuid;
                            response.contentType = fileData.mimeType;
                            response.isStorageUpdated = true;
                            return this.modalService.hideWithResponse(response);
                        }
                    })
                    .catch(() => {
                        this.busyService.stop();
                    })
                    .finally(() => {
                        this.busyService.stop();
                        this.retrievalInProgress = true;
                    })
            })
        }

        getFileData(file: IFile, croppedData: string): angular.IPromise<any> {
            if (this.showCropShape) {
                return this.$q.resolve(croppedData);
            } else {
                // convert blob to base64
                return this.Upload.dataUrl(file, true);
            }
        }

        revertImage(): void {
            this.udfImageTemplate = false;
            this.fetchConfig();
        }

        cancel(): void {
            this.modalService.cancel();
        };

    }

    angular.module('meevo.components.udfImageUpload')
        .component('udfImageUploadComponent', <angular.IComponentOptions>{
            templateUrl: 'app/components/udf-image-upload-component/udf-image-upload.component.html',
            controller: UdfImageUploadComponent,
            controllerAs: '$ctrl',
            bindings: {
                fileType: '=',
                meevoFile: '=',
                fileGuid: '=',
                isStorageUpdated: '=?',
                mimeType: '=?',
                retrievalInProgress: '=?',
                udfImageTemplate: '=?',
                udfImageDateUpdated: '=?',
                revertIcon: '=?',
                upload: '&',
                ngDisabled: '=?'
            }
        });
}
